import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import settingsService, { ContactFormPublishMethod } from "api/settingsService";
import { ENotificationType, notification } from "utils/notification";
import { IContactFormWizardSettings, WizardSettingsPatch } from "components/ContactFormWizard/typings";
import CopyIcon from "assets/icons/copy.svg?react";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import wizardStyles from "components/ContactFormWizard/styles.module.scss";
import styles from "./PublishLeftPanel.module.scss";

interface MethodRowProps {
  id: ContactFormPublishMethod;
  label: string;
  description: string;
  snippet: string;
  selected: boolean;
  onSelect: () => void;
  onCopy: () => void;
}

const MethodRow = ({ label, description, snippet, selected, onSelect, onCopy }: MethodRowProps) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${styles.methodRow} ${selected ? styles.methodRowActive : ""}`}
      onClick={onSelect}
      role="button"
    >
      <div className={styles.methodLabel}>
        {label}
      </div>
      <div className={styles.methodDesc}>{description}</div>
      <div className={wizardStyles.copyBlock}>
        <div className={wizardStyles.code}>
          <span className={wizardStyles.codeText}>{snippet || "—"}</span>
          <button
            type="button"
            className={wizardStyles.codeCopyBtn}
            onClick={onCopy}
            aria-label={t("common.copy")}
            title={t("common.copy")}
            disabled={!snippet}
          >
            <CopyIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

interface Props {
  settings: IContactFormWizardSettings;
  patchSettingsRef: React.MutableRefObject<(patch: WizardSettingsPatch) => void>;
  finishRef: React.MutableRefObject<() => void>;
  onGoBack: () => void;
}

const PublishLeftPanel = ({ settings, patchSettingsRef, finishRef, onGoBack }: Props) => {
  const { t } = useTranslation();

  const { data: formData } = settingsService.useGetForm(settings.WorkspaceId);
  const key = formData?.data?.[0]?.Key;
  const base = import.meta.env.VITE_APP_FORM_SITE;
  const link = useMemo(() => (key ? `${base}?id=${key}` : ""), [key, base]);
  const iframeSnippet = useMemo(
    () => (link ? `<iframe src="${link}" width="1072px" height="416px"></iframe>` : ""),
    [link],
  );

  const copyToClipboard = async (text: string) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    notification({
      type: ENotificationType.INFO,
      title: t("settings.copiedToClipboard"),
      text: t("settings.embedCodeCopied"),
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.hint}>
        <p>
          Chciałbyś, aby Twoi kontrahenci mogli bezpiecznie i poufnie wysyłać do Ciebie swoje
          dokumenty? Jeśli tak, pobierz formularz kontaktowy i osadź go bezpośrednio na swojej
          stronie www. Zacznij od pobrania kodu iFrame.
        </p>
      </div>
      <div className={styles.methods}>
        <MethodRow
          id="iframe"
          label={t("contactFormWizard.steps.publish.iframeLabel")}
          description={t("contactFormWizard.steps.publish.iframeHint")}
          snippet={iframeSnippet}
          selected={settings.PublishMethod === "iframe"}
          onSelect={() => patchSettingsRef.current({ PublishMethod: "iframe" })}
          onCopy={() => copyToClipboard(iframeSnippet)}
        />
      </div>

      <div className={styles.actions}>
        <HollowButton text={t("common.back")} onClick={onGoBack} />
        <Button
          text={t("contactFormWizard.steps.publish.publish")}
          onClick={() => finishRef.current()}
          disabled={!link}
        />
      </div>
    </div>
  );
};

export default PublishLeftPanel;
